import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBuilding,
  FaCalendarCheck,
  FaChevronRight,
  FaExclamationCircle,
  FaFolderOpen,
  FaPlus,
  FaUserFriends,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { formatPhone } from "../utils/formatters";
import PortalAlert from "./PortalAlert";
import { getLeadStatusTone } from "./PortalStatusSelect";
import { getPortalSession, isModuleEnabled, isPortalOwner } from "./portalSession";

const EMPTY_LIST_RESPONSE = { data: { data: [] } };

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfToday() {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

function parseDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isTodayFollowup(value) {
  const d = parseDate(value);
  if (!d) return false;
  return d >= startOfToday() && d <= endOfToday();
}

function isOverdueFollowup(value) {
  const d = parseDate(value);
  if (!d) return false;
  return d < startOfToday();
}

function formatFollowupWhen(value) {
  const d = parseDate(value);
  if (!d) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDisplayDate(d = new Date()) {
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function fullName(l) {
  return `${(l.First_Name || "").trim()} ${(l.Last_Name || "").trim()}`.trim() || "—";
}

function phoneDisplay(l) {
  if (!l?.Mobile_Number) return "—";
  const digits = `${l.Country_Code || ""}${l.Mobile_Number}`.replace(/\D/g, "");
  return formatPhone(digits) || "—";
}

function greetingForNow() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function TenantDashboard({ modules = null }) {
  const navigate = useNavigate();
  const session = useMemo(() => getPortalSession(), []);
  const portalOwner = useMemo(() => isPortalOwner(session), [session]);
  const firstName = String(session?.First_Name || "").trim();
  const showClients = isModuleEnabled(modules, "clients");
  const showCustomers = isModuleEnabled(modules, "customers");
  const showProjects = isModuleEnabled(modules, "projects");

  const [leads, setLeads] = useState([]);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [clientCount, setClientCount] = useState(0);
  const [customerCount, setCustomerCount] = useState(0);
  const [projectCount, setProjectCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const listOrEmpty = (enabled, url) =>
          enabled
            ? axiosInstance.get(url).catch(() => EMPTY_LIST_RESPONSE)
            : Promise.resolve(EMPTY_LIST_RESPONSE);
        const [leadsRes, employeesRes, clientsRes, customersRes, projectsRes] = await Promise.all([
          listOrEmpty(true, "/client/leads"),
          listOrEmpty(portalOwner, "/client/employees"),
          listOrEmpty(showClients, "/client/tenant-clients"),
          listOrEmpty(showCustomers, "/client/tenant-customers"),
          listOrEmpty(showProjects, "/client/tenant-projects"),
        ]);
        if (!active) return;
        setLeads(Array.isArray(leadsRes.data?.data) ? leadsRes.data.data : []);
        setEmployeeCount(
          (Array.isArray(employeesRes.data?.data) ? employeesRes.data.data : []).length
        );
        setClientCount((Array.isArray(clientsRes.data?.data) ? clientsRes.data.data : []).length);
        setCustomerCount(
          (Array.isArray(customersRes.data?.data) ? customersRes.data.data : []).length
        );
        setProjectCount(
          (Array.isArray(projectsRes.data?.data) ? projectsRes.data.data : []).length
        );
      } catch (err) {
        if (!active) return;
        setError(err.response?.data?.message || "Could not load dashboard data.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [portalOwner, showClients, showCustomers, showProjects]);

  const todayFollowups = useMemo(() => {
    return leads
      .filter((l) => isTodayFollowup(l.Last_Next_Followup_Date))
      .sort((a, b) => {
        const at = parseDate(a.Last_Next_Followup_Date)?.getTime() || 0;
        const bt = parseDate(b.Last_Next_Followup_Date)?.getTime() || 0;
        return at - bt;
      });
  }, [leads]);

  const overdueFollowups = useMemo(() => {
    return leads
      .filter((l) => isOverdueFollowup(l.Last_Next_Followup_Date))
      .sort((a, b) => {
        const at = parseDate(a.Last_Next_Followup_Date)?.getTime() || 0;
        const bt = parseDate(b.Last_Next_Followup_Date)?.getTime() || 0;
        return at - bt;
      });
  }, [leads]);

  const cards = [
    {
      key: "today",
      label: "Today's Follow-ups",
      value: todayFollowups.length,
      path: null,
      icon: FaCalendarCheck,
      tone: "followup",
      scrollTo: "todays-followups",
    },
    portalOwner && {
      key: "employees",
      label: "Employees",
      value: employeeCount,
      path: "/portal/employees",
      icon: FaUsers,
      tone: "employees",
    },
    {
      key: "leads",
      label: "Leads",
      value: leads.length,
      path: "/portal/leads",
      icon: FaUserPlus,
      tone: "leads",
    },
    showClients && {
      key: "clients",
      label: "Clients",
      value: clientCount,
      path: "/portal/clients",
      icon: FaBuilding,
      tone: "clients",
    },
    showCustomers && {
      key: "customers",
      label: "Customers",
      value: customerCount,
      path: "/portal/customers",
      icon: FaUserFriends,
      tone: "customers",
    },
    showProjects && {
      key: "projects",
      label: "Projects",
      value: projectCount,
      path: "/portal/projects",
      icon: FaFolderOpen,
      tone: "projects",
    },
  ].filter(Boolean);

  const onStatClick = (card) => {
    if (card.scrollTo) {
      const el = document.getElementById(card.scrollTo);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (card.path) navigate(card.path);
  };

  return (
    <div className="cportal-page cportal-dashboard">
      <section className="cportal-dash-hero">
        <div className="cportal-dash-hero-copy">
          <p className="cportal-dash-kicker">{formatDisplayDate()}</p>
          <h1>
            {greetingForNow()}
            {firstName ? `, ${firstName}` : ""}
          </h1>
        </div>
        <div className="cportal-dash-hero-actions">
          <button
            type="button"
            className="cportal-btn cportal-btn-primary"
            onClick={() => navigate("/portal/leads/new")}
          >
            <FaPlus aria-hidden="true" /> Add lead
          </button>
          <button
            type="button"
            className="cportal-btn cportal-btn-edit"
            onClick={() => navigate("/portal/leads")}
          >
            View leads
          </button>
        </div>
      </section>

      <PortalAlert type="error" message={error} onClear={() => setError("")} />

      {overdueFollowups.length > 0 && !loading && (
        <div className="cportal-dash-overdue" role="status">
          <FaExclamationCircle aria-hidden="true" />
          <div>
            <strong>
              {overdueFollowups.length} overdue follow-up
              {overdueFollowups.length === 1 ? "" : "s"}
            </strong>
          </div>
          <button
            type="button"
            className="cportal-btn cportal-btn-ghost cportal-btn-sm"
            onClick={() => navigate("/portal/leads")}
          >
            Open leads
          </button>
        </div>
      )}

      <div className="cportal-stats cportal-dash-stats">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <button
              key={card.key}
              type="button"
              className={`cportal-stat-card cportal-stat-card--${card.tone}`}
              onClick={() => onStatClick(card)}
            >
              <span className="cportal-stat-icon" aria-hidden="true">
                <Icon />
              </span>
              <span className="cportal-stat-value">{loading ? "—" : card.value}</span>
              <span className="cportal-stat-label">{card.label}</span>
            </button>
          );
        })}
      </div>

      <section id="todays-followups" className="cportal-card cportal-dash-followups">
        <div className="cportal-card-head cportal-dash-section-head">
          <div>
            <h2>Today&apos;s Follow-ups</h2>
          </div>
          <span className="cportal-dash-section-count">
            {loading ? "—" : todayFollowups.length}
          </span>
        </div>

        {loading ? (
          <p className="cportal-muted">Loading follow-ups…</p>
        ) : todayFollowups.length === 0 ? (
          <div className="cportal-dash-empty">
            <FaCalendarCheck aria-hidden="true" />
            <div>
              <strong>No follow-ups scheduled for today</strong>
            </div>
            <button
              type="button"
              className="cportal-btn cportal-btn-edit"
              onClick={() => navigate("/portal/leads")}
            >
              Browse leads
            </button>
          </div>
        ) : (
          <ul className="cportal-dash-followup-list">
            {todayFollowups.map((l) => {
              const tone = getLeadStatusTone(l.Lead_Status);
              return (
                <li key={l.Real_Estate_Lead_Id}>
                  <button
                    type="button"
                    className="cportal-dash-followup-row"
                    onClick={() => navigate(`/portal/leads/${l.Real_Estate_Lead_Id}`)}
                  >
                    <div className="cportal-dash-followup-main">
                      <strong>{fullName(l)}</strong>
                      <span className="cportal-muted">{phoneDisplay(l)}</span>
                    </div>
                    <div className="cportal-dash-followup-meta">
                      <span className="cportal-dash-followup-time">
                        {formatFollowupWhen(l.Last_Next_Followup_Date)}
                      </span>
                      <span
                        className={
                          tone === "neutral"
                            ? "cportal-badge"
                            : `cportal-badge cportal-badge--${tone}`
                        }
                      >
                        {l.Lead_Status || "No status"}
                      </span>
                    </div>
                    <p className="cportal-dash-followup-notes" title={l.Last_Followup_Notes || ""}>
                      {l.Last_Followup_Notes || "No follow-up notes yet"}
                    </p>
                    <FaChevronRight className="cportal-dash-followup-chevron" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

export default TenantDashboard;
